/**
 * Admin → one user (PRP-MCP-9 work item 8, G7, founder decision F8): everything
 * about one user's access in one place, and grants set from here.
 *
 * Sections: account, subscription (plan, status, Stripe ids, founder flag and
 * end), trial (dates, refused / refund state), grants (feature, source, end
 * date, revoke), credits (included this month, pack balance, the ledger), consent
 * (version, date), chat estimates (name, created, from chat, export date, import
 * answer, link), snapshots (count, link to the review page for this user).
 *
 * Add grant: a feature from the server's list and an end date from its presets
 * (30 days, 90 days, no end) or a custom date. The server computes the date and
 * writes it through `grant_entitlement`. Admin only on the server: a non-admin
 * gets 403 and sees "Admins only."
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { AdminUserAccessAPI } from '../../lib/adminUserAccessApi';
import type { AccessComparisons, AccessGrant, UserAccess } from '../../lib/adminUserAccessApi';

const CUSTOM = 'custom';
const ANSWER_LABEL: Record<string, string> = { yes: 'Yes', no: 'No', not_yet: 'Not yet' };

const fmtDate = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString() : '—');
const fmtTime = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString() : '—');
const dash = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : String(v));

function errText(err: unknown, fallback: string): string {
  const e = err as { message?: string; status?: number; response?: { data?: { detail?: string } } };
  if (e?.status === 403) return 'Admins only.';
  return e?.response?.data?.detail || e?.message || fallback;
}

/** One titled card; `missing` when the server could not read that section. */
const Section: React.FC<{ id: string; title: string; missing?: boolean; right?: React.ReactNode; children: React.ReactNode }> = ({
  id, title, missing, right, children,
}) => (
  <section className="space-y-2 rounded-md border border-border p-4" data-testid={`section-${id}`}>
    <div className="flex items-center gap-3">
      <h2 className="text-base font-semibold">{title}</h2>
      {right && <div className="ml-auto">{right}</div>}
    </div>
    {missing ? <p className="text-sm text-muted-foreground">Could not read this section.</p> : children}
  </section>
);

const Fields: React.FC<{ rows: [string, React.ReactNode][] }> = ({ rows }) => (
  <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-[12rem_1fr]">
    {rows.map(([k, v]) => (
      <React.Fragment key={k}>
        <dt className="text-muted-foreground">{k}</dt>
        <dd className="break-all">{v}</dd>
      </React.Fragment>
    ))}
  </dl>
);

/** The Add grant form: feature, end-date preset or custom date. */
const AddGrantForm: React.FC<{ access: UserAccess; onAdded: () => void }> = ({ access, onAdded }) => {
  const [feature, setFeature] = useState(access.valid_features[0] ?? '');
  const [preset, setPreset] = useState(access.grant_presets[0]?.id ?? '');
  const [endDate, setEndDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const submit = async () => {
    if (preset === CUSTOM && !endDate) {
      setMsg('Pick an end date.');
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const res = await AdminUserAccessAPI.addGrant(access.user_id, {
        feature,
        preset,
        ...(preset === CUSTOM ? { end_date: endDate } : {}),
      });
      setMsg(`Granted ${res.feature}${res.expires_at ? ` until ${fmtDate(res.expires_at)}` : ' with no end date'}.`);
      onAdded();
    } catch (err) {
      setMsg(errText(err, 'Could not add the grant.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 border-t border-border pt-3" data-testid="add-grant">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <label className="text-muted-foreground" htmlFor="grant-feature">Feature</label>
        <select
          id="grant-feature"
          aria-label="Feature"
          className="rounded-md border border-border bg-background px-2 py-1 text-sm"
          value={feature}
          onChange={(e) => setFeature(e.target.value)}
        >
          {access.valid_features.map((f) => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
        <span className="text-muted-foreground">Ends</span>
        {access.grant_presets.map((p) => (
          <button
            key={p.id}
            type="button"
            aria-pressed={preset === p.id}
            className={`rounded-md border px-2 py-1 ${preset === p.id ? 'border-primary bg-primary/10' : 'border-border'}`}
            onClick={() => setPreset(p.id)}
          >
            {p.label}
          </button>
        ))}
        <input
          type="date"
          aria-label="Custom end date"
          className={`rounded-md border bg-background px-2 py-1 ${preset === CUSTOM ? 'border-primary' : 'border-border'}`}
          value={endDate}
          onChange={(e) => {
            setEndDate(e.target.value);
            setPreset(CUSTOM);
          }}
        />
        <button
          type="button"
          disabled={busy || !feature || !preset}
          className="rounded-md bg-primary px-3 py-1 text-primary-foreground disabled:opacity-50"
          onClick={() => void submit()}
        >
          Add grant
        </button>
      </div>
      {msg && <p className="text-sm text-muted-foreground">{msg}</p>}
    </div>
  );
};

/** PRP-MCP-10 G5: comparisons used and cap; raise this one user's cap (blank = back to the default). */
const ComparisonsCap: React.FC<{ userId: string; data: AccessComparisons; onSaved: () => void }> = ({ userId, data, onSaved }) => {
  const [value, setValue] = useState(data.override === null ? '' : String(data.override));
  const [msg, setMsg] = useState<string | null>(null);
  const save = async () => {
    const n = value.trim() === '' ? null : Number(value);
    if (n !== null && (!Number.isInteger(n) || n < 0)) {
      setMsg('Enter a whole number, or leave blank for the default.');
      return;
    }
    try {
      await AdminUserAccessAPI.setComparisonCap(userId, n);
      setMsg(n === null ? 'Back to the default cap.' : `Cap set to ${n}.`);
      onSaved();
    } catch (err) {
      setMsg(errText(err, 'Could not set the cap.'));
    }
  };
  return (
    <div className="space-y-2">
      <p className="text-sm" data-testid="comparisons-used">
        {data.used} of {data.unlimited ? 'unlimited (admin)' : data.cap} comparisons used
        {data.override === null ? ` (default cap ${data.default_cap})` : ` (cap raised from ${data.default_cap})`}.
      </p>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <label htmlFor="comparison-cap" className="text-muted-foreground">Cap for this user</label>
        <input
          id="comparison-cap"
          type="number"
          min={0}
          className="w-24 rounded-md border border-border bg-background px-2 py-1"
          value={value}
          placeholder={String(data.default_cap)}
          onChange={(e) => setValue(e.target.value)}
        />
        <button type="button" className="rounded-md bg-primary px-3 py-1 text-primary-foreground" onClick={() => void save()}>
          Set cap
        </button>
      </div>
      {msg && <p className="text-sm text-muted-foreground">{msg}</p>}
    </div>
  );
};

const GrantsTable: React.FC<{ grants: AccessGrant[]; onRevoke: (feature: string) => void }> = ({ grants, onRevoke }) =>
  grants.length === 0 ? (
    <p className="text-sm text-muted-foreground">No grants.</p>
  ) : (
    <table className="w-full text-sm">
      <thead className="text-left text-xs text-muted-foreground">
        <tr><th className="p-1">Feature</th><th className="p-1">Source</th><th className="p-1">Ends</th><th className="p-1" /></tr>
      </thead>
      <tbody>
        {grants.map((g) => (
          <tr key={g.feature} className={`border-t border-border ${g.active ? '' : 'text-muted-foreground'}`}>
            <td className="p-1 font-mono">{g.feature}</td>
            <td className="p-1">{dash(g.source)}</td>
            <td className="p-1">{g.expires_at ? fmtDate(g.expires_at) : 'No end date'}{g.active ? '' : ' (expired)'}</td>
            <td className="p-1 text-right">
              <button
                type="button"
                className="rounded-md border border-red-500/50 px-2 py-0.5 text-xs text-red-600 dark:text-red-400"
                onClick={() => onRevoke(g.feature)}
              >
                Revoke {g.feature}
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

/** The /admin/users/:id page. */
const AdminUserPage: React.FC = () => {
  const { id = '' } = useParams();
  const [access, setAccess] = useState<UserAccess | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showLedger, setShowLedger] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setAccess(await AdminUserAccessAPI.get(id));
    } catch (err) {
      setError(errText(err, 'Could not load this user.'));
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const revoke = async (feature: string) => {
    if (!window.confirm(`Revoke ${feature} from ${access?.account.email || id}?`)) return;
    try {
      await AdminUserAccessAPI.revokeGrant(id, feature);
      await load();
    } catch (err) {
      setError(errText(err, 'Could not revoke the grant.'));
    }
  };

  const header = (
    <div className="flex flex-wrap items-center gap-3">
      <h1 className="text-xl font-semibold">{access?.account.email || id}</h1>
      <Link to="/admin?tab=users" className="text-sm text-muted-foreground underline">Users</Link>
    </div>
  );
  if (!access) {
    return (
      <div className="space-y-4">
        {header}
        <p className={error ? 'text-sm font-medium text-red-500' : 'text-sm text-muted-foreground'}>{error || 'Loading…'}</p>
      </div>
    );
  }
  const gone = (s: string) => access.unavailable.includes(s);
  const { account: a, subscription: sub, trial: t, credits: cr, consent: c, snapshots: snaps } = access;

  return (
    <div className="space-y-4">
      {header}
      {error && <p className="text-sm font-medium text-red-500">{error}</p>}
      <Section id="account" title="Account">
        <Fields rows={[
          ['Email', dash(a.email)], ['Name', dash(a.name)], ['Created', fmtDate(a.created_at)],
          ['Last login', fmtTime(a.last_login)], ['Status', a.is_active ? 'Active' : 'Deactivated'],
        ]} />
      </Section>
      <Section id="subscription" title="Subscription" missing={gone('subscription')}>
        {sub ? (
          <Fields rows={[
            ['Plan', `${dash(sub.plan_name)}${sub.active ? '' : ' (not active)'}`], ['Status', dash(sub.status)],
            ['Stripe customer', dash(sub.stripe_customer_id)], ['Stripe subscription', dash(sub.stripe_subscription_id)],
            ['Period ends', fmtDate(sub.current_period_end)],
            ['Founder', sub.founder ? `Yes, until ${fmtDate(sub.founder_until)}${sub.founder_active ? '' : ' (ended)'}` : 'No'],
          ]} />
        ) : <p className="text-sm text-muted-foreground">No subscription.</p>}
      </Section>
      <Section id="trial" title="Trial" missing={gone('trial')}>
        {t ? (
          <Fields rows={[
            ['Status', dash(t.status)], ['Started', fmtDate(t.started_at || t.trial_start)], ['Trial ends', fmtDate(t.trial_end)],
            ['Converted', fmtDate(t.converted_at)], ['Canceled', fmtDate(t.canceled_at)],
            ...(t.refused_at ? ([
              ['Refused', `${fmtDate(t.refused_at)}: card used by an earlier trial`],
              ['Refund', t.refund_state === 'refunded' ? `Refunded (${dash(t.refund_id)})`
                : t.refund_state === 'refund_failed' ? `Refund failed, still owed $7: ${dash(t.refund_error)}` : 'Pending'],
            ] as [string, React.ReactNode][]) : []),
          ]} />
        ) : <p className="text-sm text-muted-foreground">No trial.</p>}
      </Section>
      <Section id="grants" title="Grants" missing={gone('grants')}>
        <GrantsTable grants={access.grants ?? []} onRevoke={(f) => void revoke(f)} />
        <AddGrantForm access={access} onAdded={() => void load()} />
      </Section>
      <Section
        id="credits"
        title="Credits"
        missing={gone('credits')}
        right={cr && (
          <button type="button" className="text-sm underline" onClick={() => setShowLedger((v) => !v)}>
            {showLedger ? 'Hide ledger' : `Ledger (${cr.ledger.length})`}
          </button>
        )}
      >
        {cr && (
          <>
            <Fields rows={[
              ['Included this month', `${cr.included} of ${cr.included_per_month}`], ['Pack credits', String(cr.pack)],
              ['Total', String(cr.total)],
            ]} />
            {showLedger && (
              <table className="w-full text-xs" data-testid="ledger">
                <thead className="text-left text-muted-foreground">
                  <tr><th className="p-1">When</th><th className="p-1">Reason</th><th className="p-1">Pack</th><th className="p-1">Included</th><th className="p-1">From</th><th className="p-1">Ref</th></tr>
                </thead>
                <tbody>
                  {cr.ledger.map((r, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="p-1">{fmtTime(r.created_at)}</td><td className="p-1">{dash(r.reason)}</td>
                      <td className="p-1">{dash(r.delta)}</td><td className="p-1">{dash(r.included_delta)}</td>
                      <td className="p-1">{dash(r.source)}</td><td className="p-1 font-mono">{dash(r.ref)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </Section>
      <Section id="consent" title="Assistant consent" missing={gone('consent')}>
        {c && (c.accepted ? (
          <p className="text-sm">
            Accepted version {c.version} on {fmtDate(c.accepted_at)}
            {c.is_current ? '' : ` (current text is version ${c.current_version}; not accepted yet)`}
          </p>
        ) : <p className="text-sm">Not accepted.</p>)}
      </Section>
      <Section id="chat-estimates" title="Chat estimates" missing={gone('chat_estimates')}>
        {(access.chat_estimates ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">None.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground">
              <tr><th className="p-1">Name</th><th className="p-1">Created</th><th className="p-1">From chat</th><th className="p-1">Last export</th><th className="p-1">Import answer</th></tr>
            </thead>
            <tbody>
              {(access.chat_estimates ?? []).map((e) => (
                <tr key={e.estimate_id} className="border-t border-border">
                  <td className="p-1"><Link to={e.link} className="underline">{e.name}</Link></td>
                  <td className="p-1">{fmtDate(e.created_at)}</td>
                  <td className="p-1">{e.from_chat === 'created' ? 'Created in chat' : 'Changed in chat'}</td>
                  <td className="p-1">{e.last_export_at ? fmtDate(e.last_export_at) : 'Not exported'}</td>
                  <td className="p-1">{e.import_answer ? ANSWER_LABEL[e.import_answer] : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
      {access.comparisons && (
        <Section id="comparisons" title="Comparisons" missing={gone('comparisons')}>
          <ComparisonsCap key={`${access.comparisons.used}-${access.comparisons.cap}`} userId={access.user_id}
            data={access.comparisons} onSaved={() => void load()} />
        </Section>
      )}
      <Section id="referral" title="Referred by" missing={gone('referral')}>
        {access.referral ? (
          <p className="text-sm">
            <Link to={access.referral.link} className="underline">
              {dash(access.referral.name)} ({dash(access.referral.code)})
            </Link>{' '}
            on {fmtDate(access.referral.referred_at)}
          </p>
        ) : <p className="text-sm text-muted-foreground">No referral.</p>}
      </Section>
      <Section id="snapshots" title="Snapshots" missing={gone('snapshots')}>
        {snaps && (
          <p className="text-sm">
            {snaps.count} snapshot{snaps.count === 1 ? '' : 's'}.{' '}
            <Link to={snaps.review_link} className="underline">Open in review</Link>
          </p>
        )}
      </Section>
    </div>
  );
};

export default AdminUserPage;
